/*
 * Open Box Warehouse Event page: shared behavior for the warehouse-* sections.
 *
 * - Stock sync: fills the [units]/[models] token spans and the final-days
 *   list from the catalog cards ([data-wh-model][data-wh-units]).
 * - Booking: loads Cal.com's embed once, reports Book clicks and bookings to
 *   window.dataLayer, and sends visitors to Cal.com's hosted page while the
 *   embed is still loading or if it failed.
 * - Theme editor: opens the FAQ item you select.
 *
 * Each section that needs it includes this file, so it guards against
 * running twice.
 */
(() => {
  if (window.WarehouseEvent) return;
  window.WarehouseEvent = { version: 1 };

  /* Stock sync ----------------------------------------------------------- */

  function syncStock(ignoredRoot) {
    const models = Array.from(document.querySelectorAll('[data-wh-model][data-wh-units]'))
      .filter((card) => !(ignoredRoot && ignoredRoot.contains(card)))
      .map((card) => ({
        code: card.dataset.whModel,
        units: Math.max(0, parseInt(card.dataset.whUnits, 10) || 0),
      }));
    const hasCards = models.length > 0;
    const total = models.reduce((sum, model) => sum + model.units, 0);
    const available = models.filter((model) => model.units > 0).length;

    document.querySelectorAll('[data-wh-total-units]').forEach((el) => {
      el.textContent = hasCards ? String(total) : '';
    });
    document.querySelectorAll('[data-wh-model-count]').forEach((el) => {
      el.textContent = hasCards ? String(available) : '';
    });
    document.querySelectorAll('[data-wh-units-by-model]').forEach((list) => {
      const soldOut = list.dataset.whSoldOutLabel || 'sold out';
      list.replaceChildren(
        ...models.map((model) => {
          const item = document.createElement('span');
          item.className = 'wh-strip__item';
          item.textContent = `${model.code} ${model.units > 0 ? model.units : soldOut}`;
          return item;
        })
      );
    });
  }

  /* Booking (Cal.com) ----------------------------------------------------- */

  const NAMESPACE = 'warehouse';
  const booking = { state: 'idle', model: '', reported: false };

  function track(event) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, warehouse_model: booking.model });
  }

  function originOf(trigger) {
    return (trigger.dataset.calOrigin || 'https://app.cal.com').trim().replace(/\/+$/, '');
  }

  // The hosted page reads the same query parameters the embed sends, so the
  // button's data-cal-config (preselected model, layout) carries over.
  function hostedBookingUrl(trigger) {
    const site = originOf(trigger).replace('://app.', '://');
    let config = {};
    try {
      config = JSON.parse(trigger.dataset.calConfig || '{}');
    } catch (error) {
      // Open the booking page without preselected answers.
    }
    const query = new URLSearchParams(config).toString();
    return `${site}/${trigger.dataset.calLink}${query ? `?${query}` : ''}`;
  }

  // Cal.com's standard queue stub, created already marked as loaded so it
  // never injects its own copy of embed.js. loadCal() adds the only copy.
  function installCalStub() {
    if (window.Cal) return;
    const queue = (api, args) => api.q.push(args);
    const Cal = function () {
      const args = arguments;
      if (args[0] === 'init') {
        const api = function () {
          queue(api, arguments);
        };
        const namespace = args[1];
        api.q = api.q || [];
        if (typeof namespace === 'string') {
          Cal.ns[namespace] = Cal.ns[namespace] || api;
          queue(Cal.ns[namespace], args);
          queue(Cal, ['initNamespace', namespace]);
        } else {
          queue(Cal, args);
        }
        return;
      }
      queue(Cal, args);
    };
    Cal.loaded = true;
    Cal.ns = {};
    Cal.q = [];
    window.Cal = Cal;
  }

  function loadCal(trigger) {
    if (booking.state !== 'idle') return;
    booking.state = 'loading';
    const origin = originOf(trigger);
    let layout = 'month_view';
    try {
      layout = JSON.parse(trigger.dataset.calConfig || '{}').layout || layout;
    } catch (error) {
      // Keep the default layout.
    }

    if (window.Cal) {
      // Another Cal.com embed on the page already loads embed.js. Loading it
      // twice would open two popups per click. Assumes that embed is ready
      // (nothing else on this store loads Cal.com today); if it weren't, a
      // click before it loads would do nothing instead of using the fallback.
      booking.state = 'ready';
    } else {
      installCalStub();
      const script = document.createElement('script');
      script.src = `${origin}/embed/embed.js`;
      script.async = true;
      script.onload = () => {
        booking.state = 'ready';
      };
      script.onerror = () => {
        booking.state = 'failed';
      };
      document.head.appendChild(script);
    }

    window.Cal('init', NAMESPACE, { origin });
    const cal = window.Cal.ns[NAMESPACE];
    cal('ui', { hideEventTypeDetails: false, layout });
    cal('preload', { calLink: trigger.dataset.calLink });
    const onBooked = () => {
      if (booking.reported) return;
      booking.reported = true;
      track('warehouse_booking_success');
    };
    // bookingSuccessfulV2 replaces the deprecated bookingSuccessful; listen to
    // both and report once per booking.
    cal('on', { action: 'bookingSuccessfulV2', callback: onBooked });
    cal('on', { action: 'bookingSuccessful', callback: onBooked });
  }

  function initBooking() {
    const trigger = document.querySelector('[data-cal-link][data-warehouse-book]');
    if (trigger) loadCal(trigger);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-cal-link][data-warehouse-book]') : null;
    if (!trigger) return;
    booking.model = trigger.dataset.warehouseBook || '';
    booking.reported = false;
    track('warehouse_booking_open');
    if (booking.state === 'ready') return; // Cal.com's own click handler opens the popup.
    event.preventDefault();
    window.location.href = hostedBookingUrl(trigger);
  });

  /* Theme editor ----------------------------------------------------------- */

  document.addEventListener('shopify:block:select', (event) => {
    const item = event.target.closest('.wh-faq__item');
    if (item) item.open = true;
  });

  /* Start ------------------------------------------------------------------ */

  function init() {
    syncStock();
    initBooking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  // Fires while the section is still in the page, so its cards are ignored.
  document.addEventListener('shopify:section:unload', (event) => syncStock(event.target));
})();

/* Catalog: filter tabs, gallery dots and theme-editor block select. ----- */
(() => {
  if (customElements.get('wh-catalog')) return;

  class WarehouseCatalog extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[data-wh-filter]'));
      this.cards = Array.from(this.querySelectorAll('[data-wh-card]'));
      this.onTabClick = (event) => this.applyFilter(event.currentTarget.dataset.whFilter);
      this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
      // Selecting a Model block in the theme editor shows every card and
      // scrolls to the selected one.
      this.onBlockSelect = (event) => {
        this.applyFilter('all');
        event.target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      };
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.observers = Array.from(this.querySelectorAll('[data-wh-gallery]'))
        .map((card) => this.watchDots(card))
        .filter(Boolean);
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.observers.forEach((observer) => observer.disconnect());
    }

    applyFilter(filter) {
      this.tabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.whFilter === filter)));
      this.cards.forEach((card) => {
        card.hidden = !this.cardMatches(card, filter);
      });
    }

    cardMatches(card, filter) {
      if (filter === 'all') return true;
      if (filter === 'outdoor') return card.dataset.whOutdoor === 'true';
      return filter === `people-${card.dataset.whPeople}`;
    }

    // Moves the active dot as the gallery scrolls. Scrolling and the arrows
    // come from the theme's own <scroll-carousel>.
    watchDots(card) {
      const track = card.querySelector('scroll-carousel');
      const slides = Array.from(card.querySelectorAll('.wh-slide'));
      const dots = Array.from(card.querySelectorAll('.wh-dot'));
      if (!track || dots.length < 2) return null;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // Check the ratio, not isIntersecting: WebKit and Gecko report a
            // slide that only touches the edge as intersecting.
            if (entry.intersectionRatio < 0.6) return;
            const index = slides.indexOf(entry.target);
            dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));
      return observer;
    }
  }

  customElements.define('wh-catalog', WarehouseCatalog);
})();
